import React, { useState } from 'react';
import { motion } from 'framer-motion';

const ResultDisplay = ({ result, isLoading, operation, cipherType }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const downloadResult = () => {
    if (!result) return;
    const element = document.createElement('a');
    const file = new Blob([result], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `cryptgame-${operation || 'result'}-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  if (isLoading) {
    return (
      <motion.div
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="flex items-center justify-center py-12">
          <motion.div
            className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <span className="ml-4 text-gray-600">Processing...</span>
        </div>
      </motion.div>
    );
  }

  if (!result) {
    return (
      <motion.div
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Result</h2>
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg">Your result will appear here</p>
          <p className="text-sm mt-2">Select a cipher and enter text to get started</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Result</h2>
        {operation && (
          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold capitalize">
            {operation}
          </span>
        )}
      </div>

      {cipherType && (
        <div className="mb-4">
          <span className="text-sm text-gray-600">Cipher: </span>
          <span className="text-sm font-semibold text-gray-800 capitalize">{cipherType}</span>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-4 mb-4 min-h-[150px]">
        <motion.pre
          className="text-gray-800 whitespace-pre-wrap break-words font-mono text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {result}
        </motion.pre>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <motion.button
          onClick={copyToClipboard}
          className="flex-1 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {copied ? (
            <>
              <span>✓</span>
              <span>Copied!</span>
            </>
          ) : (
            <>
              <span>📋</span>
              <span>Copy Result</span>
            </>
          )}
        </motion.button>

        <motion.button
          onClick={downloadResult}
          className="flex-1 px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <span>💾</span>
          <span>Download</span>
        </motion.button>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Result length: {result.length} characters
        </p>
      </div>
    </motion.div>
  );
};

export default ResultDisplay;
